import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Login } from './components/Login';
import { PrivateRoute } from './components/PrivateRoute';
import { RegisterPage } from './pages/RegisterPage';
import { VerifyEmailPage } from './pages/VerifyEmailPage';
import { PaymentCallbackPage } from './pages/PaymentCallbackPage';
import { ForgotPassword } from './components/ForgotPassword';
import { ResetPassword } from './components/ResetPassword';
import { BoardPage } from './components/BoardPage';
import { BoardAdmin } from './components/BoardAdmin';
import { JoinBoard } from './pages/JoinBoard';
import { ArchivedCards } from './pages/ArchivedCards';
import { ProfileSettings } from './pages/ProfileSettings';
import { AccountManagementPage } from './pages/AccountManagementPage';
import { UpgradePlanPage } from './pages/UpgradePlanPage';
import { LandingPage } from './components/LandingPage';
import { PricingPage } from './pages/PricingPage';
import AdminDashboard from './components/AdminDashboard';
import TenantDetail from './components/TenantDetail';
import UserAdminDashboard from './components/UserAdminDashboard';
import UserDetail from './components/UserDetail';
import SubscriptionPlansConfig from './components/SubscriptionPlansConfig';
import AdminExtendedDashboard from './components/AdminExtendedDashboard';
import ApiDocumentation from './pages/ApiDocumentation';
import UserDocumentation from './pages/UserDocumentation';
import { LegalPage } from './pages/LegalPage';
import { ContactPage } from './pages/ContactPage';
import { HelpPage } from './pages/HelpPage';
import { SupportPage } from './pages/SupportPage';
import { CookieBanner } from './components/CookieBanner';
import { SuperAdminRoute } from './components/SuperAdminRoute';
import './index.css';
import './styles/ads.css';

const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-kanban-blue">
        <div className="text-white text-2xl">Chargement...</div>
      </div>
    );
  }

  return !isAuthenticated ? <>{children}</> : <Navigate to="/dashboard" />;
};

const HomeRoute: React.FC = () => {
  const { loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-kanban-blue">
        <div className="text-white text-2xl">Chargement...</div>
      </div>
    );
  }

  return <LandingPage />;
};

const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

export const AppRoutes: React.FC = () => {
  return (
    <Routes>
      <Route path="/" element={<HomeRoute />} />
      <Route path="/login" element={
        <PublicRoute>
          <Login />
        </PublicRoute>
      } />
      <Route path="/register" element={
        <PublicRoute>
          <RegisterPage />
        </PublicRoute>
      } />
      <Route path="/tarifs" element={<PricingPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/payment/callback" element={<PaymentCallbackPage />} />
      <Route path="/forgot-password" element={
        <PublicRoute>
          <ForgotPassword />
        </PublicRoute>
      } />
      <Route path="/reset-password" element={
        <PublicRoute>
          <ResetPassword />
        </PublicRoute>
      } />
      <Route path="/dashboard" element={
        <PrivateRoute>
          <BoardAdmin />
        </PrivateRoute>
      } />
      <Route path="/boards/:boardId/archived" element={
        <PrivateRoute>
          <ArchivedCards />
        </PrivateRoute>
      } />
      <Route path="/profile" element={<Navigate to="/account" replace />} />
      <Route path="/account" element={
        <PrivateRoute>
          <AccountManagementPage />
        </PrivateRoute>
      } />
      <Route path="/upgrade-plan" element={
        <PrivateRoute>
          <UpgradePlanPage />
        </PrivateRoute>
      } />
      <Route path="/support" element={
        <PrivateRoute>
          <SupportPage />
        </PrivateRoute>
      } />
      {/* La gestion des tokens API est désormais centralisée dans l'onglet API
          de /account (cf. AccountManagementPage) : ancien lien direct conservé
          en redirection pour ne pas casser les favoris/liens existants. */}
      <Route path="/api-tokens" element={
        <PrivateRoute>
          <Navigate to="/account?tab=api" replace />
        </PrivateRoute>
      } />
      <Route path="/api-documentation" element={<ApiDocumentation />} />
      <Route path="/documentation" element={<UserDocumentation />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/help" element={<HelpPage />} />
      <Route path="/cgu" element={<LegalPage type="cgu" />} />
      <Route path="/cgv" element={<LegalPage type="cgv" />} />
      <Route path="/privacy" element={<LegalPage type="privacy_policy" />} />
      <Route path="/cookies-policy" element={<LegalPage type="cookies_policy" />} />
      <Route path="/advertising-policy" element={<LegalPage type="advertising_policy" />} />
      <Route path="/admin/saas" element={
        <SuperAdminRoute>
          <UserAdminDashboard />
        </SuperAdminRoute>
      } />
      <Route path="/admin/saas/users/:userId" element={
        <SuperAdminRoute>
          <UserDetail />
        </SuperAdminRoute>
      } />
      <Route path="/admin/saas/plans" element={
        <SuperAdminRoute>
          <SubscriptionPlansConfig />
        </SuperAdminRoute>
      } />
      <Route path="/admin/saas/extended" element={
        <SuperAdminRoute>
          <AdminExtendedDashboard />
        </SuperAdminRoute>
      } />
      {/* Routes anciennes pour compatibilité */}
      <Route path="/admin/saas/tenants/:tenantId" element={
        <SuperAdminRoute>
          <TenantDetail />
        </SuperAdminRoute>
      } />
      <Route path="/join/:token" element={<JoinBoard />} />
      <Route path="/boards/:boardId" element={
        <PrivateRoute>
          <BoardPage />
        </PrivateRoute>
      } />
      <Route path="/boards" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <ScrollToTop />
      <AuthProvider>
        <AppRoutes />
        <CookieBanner />
      </AuthProvider>
    </Router>
  );
};

export default App;
